import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { bannersService } from "../../lib/api/shopService";
import { BANNER_SECTION, BANNER_TYPE } from "../../types";
import { getImageUrl } from "../../utils/apiHelpers";
import {
  getBannerId,
  isBannerVisible,
  normalizeBannersList,
} from "../../utils/bannerHelpers";

const PromotionsSection = () => {
  const { data: bannersData } = useQuery({
    queryKey: ["banners", BANNER_TYPE.PROMO],
    queryFn: () =>
      bannersService.getBanners({ type: BANNER_TYPE.PROMO, visibleOnly: true }),
    enabled: true,
  });

  const bannerList = useMemo(
    () => normalizeBannersList(bannersData),
    [bannersData],
  );

  const sliderBanners = useMemo(
    () =>
      bannerList.filter(
        (b) => b.section === BANNER_SECTION.SECTION_1 && isBannerVisible(b),
      ),
    [bannerList],
  );

  if (sliderBanners.length === 0) {
    return null;
  }

  const hasMultipleSlides = sliderBanners.length > 1;

  const sliderSettings = {
    dots: false,
    arrows: false,
    infinite: hasMultipleSlides,
    speed: 600,
    variableWidth: true,
    slidesToScroll: 1,
    autoplay: hasMultipleSlides,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    pauseOnFocus: true,
    cssEase: "ease-in-out",
  };

  return (
    <section className="my-8" aria-label="Promotions">
      <div className="container mx-auto ">
        <div className="relative">
          <style jsx>{`
            .promotions-slider .slick-slide {
              padding: 0 8px;
            }
            .promotions-slider .slick-list {
              margin: 0 -8px;
              overflow: hidden;
            }
            .promotions-slider .slick-track {
              display: flex !important;
              align-items: stretch;
            }
            .promotions-slider .slick-slide > div {
              height: 100%;
            }
          `}</style>

          <Slider {...sliderSettings} className="promotions-slider">
            {sliderBanners.map((promo) => {
              const image = (
                <img
                  src={getImageUrl(promo.imagePath)}
                  alt={promo.title || "Promotion"}
                  className="w-[min(85vw,300px)] sm:w-[320px] lg:w-[380px] h-48 sm:h-52 object-cover rounded-xl border border-gray-200"
                  loading="lazy"
                  decoding="async"
                />
              );

              return (
                <div key={getBannerId(promo)}>
                  {promo.link ? (
                    <a
                      href={promo.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block"
                    >
                      {image}
                    </a>
                  ) : (
                    image
                  )}
                </div>
              );
            })}
          </Slider>
        </div>
      </div>
    </section>
  );
};

export default PromotionsSection;
